<template>
  <div class="mx-6 py-8 breakpoint-xl" data-test="confirmation-page">
    <VRow class="">
      <VCol lg="8" md="11" sm="12" xs="12">
        <span class="heading-4" data-test="confirmation-thank-you">{{
          $t("confirmation.thankYou", { firstName: firstName })
        }}</span>
      </VCol>
    </VRow>
    <VRow>
      <VCol lg="8" md="11" sm="12" xs="12">
        <span class="body-1" data-test="confirmation-payment-receive">{{ $t("confirmation.paymentReceive") }}</span>
        <router-link :to="{ name: 'Trips' }"
          ><a class="body-1 text-decoration-underline secondary--text" data-test="confirmation-redirect-link">{{
            $t("confirmation.redirectPage")
          }}</a></router-link
        >
        <span class="body-1">{{ $t("confirmation.page") }}</span>
      </VCol>
    </VRow>
    <VRow v-if="getIsPreAuthorizationPayment">
      <VCol lg="12" md="12" sm="12" xs="12" class="pt-0 pb-2">
        <SInfoblock
          color="green-color"
          prependIcon="mdi-check"
          :message="getMessage"
          prependIconColor="green"
          :height="$vuetify.breakpoint.xs ? '78px' : '55px'"
          data-test="confirmation-preauth-infoblock"
        />
      </VCol>
    </VRow>
    <VRow>
      <VCol lg="8" md="11" sm="12" xs="12">
        <VBtn color="brandingPrimary" outlined @click="$emit('toMainPage')" data-test="make-another-booking-button">
          <span class="button">
            {{ $t("confirmation.makeAnotherBooking") }}
          </span>
        </VBtn>
      </VCol>
    </VRow>
    <VRow>
      <VCol lg="8" md="11" sm="12" xs="12">
        <VCard :class="!isBookingInfoLoading ? 'd-flex flex-wrap' : ''" data-test="confirmation-bookings-container">
          <SConfirmationSkeletonLoader v-if="isBookingInfoLoading" />
        </VCard>
        <template v-if="!isBookingInfoLoading">
          <TripCard
            v-for="booking in bookings"
            :key="booking.bookingId"
            :trip="booking"
            :isLoading="false"
            :appConfig="appConfig"
            :data-test="`trip-card-${booking.bookingId}`"
          />
        </template>
      </VCol>
    </VRow>
  </div>
</template>

<script lang="ts">
export default {
  name: 'SConfirmation',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import moment from 'moment';
import BookingsService from '@slg/web-customer-shared/src/services/BookingsService';
import OpsZoneService from '@slg/web-customer-shared/src/services/OpsZoneService';
import RideTypeEnum from '@slg/web-customer-shared/src/enum/RideTypeEnum';
import StringFilter from '@slg/web-customer-shared/src/models/search/StringFilter';
import SearchRequest from '@slg/web-customer-shared/src/models/search/SearchRequest';
import SInfoblock from '@slg/web-customer-shared/src/components/common/SInfoblock.vue';
import PaymentTypeEnum from '@slg/web-customer-shared/src/enum/PaymentTypeEnum';
import TripCard from '@slg/web-customer-shared/src/components/common/tripCard/TripCard.vue';
import { useI18n, useVm } from '../../composables';
import SConfirmationSkeletonLoader from './SConfirmationSkeletonLoader.vue';

const props = defineProps({
  appConfig: {
    type: Object,
    default: null,
  },
  orderId: {
    type: String,
    default: '9b04b7e8-00b5-4143-b14f-87a1603223c2',
  },
  firstName: {
    type: String,
    default: null,
  },
  departurePlace: {
    type: Object,
    default: () => {},
  },
  arrivalPlace: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['toMainPage', 'handleServerError']);

const { t } = useI18n();
const vm = useVm();

const order = ref({});
const isBookingInfoLoading = ref(false);
const bookings = ref([]);
const opsZoneId = ref(null);

function addOpsZoneOptionsToTrip(opsZone) {
  bookings.value = [
    ...bookings.value.map((booking) => ({
      ...booking,
      haveShuttleTypeOptions:
        !props.appConfig.isNightCardsSupport &&
        opsZone.shuttleType === RideTypeEnum.BOTH,
      haveVehicleOptions: opsZone.vehicleCategories.length > 1,
      timeZone: opsZone.timeZone,
    })),
  ];
}

function buildBookingsSearchRequest() {
  const searchRequest = new SearchRequest(
    'dateTime',
    false,
    vm.limit,
    vm.offset
  );
  searchRequest.addStrFilters([
    new StringFilter('orderId', [props.orderId], true),
  ]);
  return searchRequest;
}

function buildOpsZoneSearchRequest() {
  const searchRequest = new SearchRequest(
    '',
    false,
    vm.limit,
    vm.offset
  );
  searchRequest.addStrFilters([
    new StringFilter('opsZoneId', [bookings.value[0].opsZoneId], true),
  ]);
  return searchRequest;
}

const getTripInfo = computed(() => ({
  adultsCount: vm.adultsCount,
  animalsTransportation: vm.animalsTransportation,
  anotherPersonIsRiding: vm.anotherPersonIsRiding,
  bookingStatus: vm.bookingStatus,
}));

const getIsPreAuthorizationPayment = computed(() => vm.paymentType === PaymentTypeEnum.PRE_AUTHORIZATION);

const getMessage = computed(() => {
  const today = moment();
  const tripDate = moment(vm.selectedRides[0].dateState.time);
  const daysToHoldBookingAmount = 5;
  const daysDifference = tripDate.diff(today, 'days');
  return daysDifference >= daysToHoldBookingAmount
    ? t('confirmation.messageIfTheTripDateIs5OrMoreDaysFromToday')
    : t('confirmation.messageIfTheTripDateIs4OrFewerDaysFromToday');
});

async function loadConfirmation() {
  isBookingInfoLoading.value = true;
  try {
    const bookingsSearchRequest = buildBookingsSearchRequest();
    const { data } = await BookingsService.bookingsSearch(
      bookingsSearchRequest
    );

    const returnBookingDateTime =
      data.bookings.length > 1 ? data.bookings[1].estimatedPickUpTime : null;

    bookings.value = data.bookings.map((booking) => ({
      ...booking,
      returnTripDateTime:
        booking.roundTrip && !booking.round ? returnBookingDateTime : null,
      stops: booking.round
        ? booking.stops.map((stop, index) =>
            !index
              ? {
                  ...stop,
                  poiName: props.arrivalPlace.poiName,
                  poiStopName: props.arrivalPlace.poiStopName,
                  address: props.arrivalPlace.address,
                }
              : {
                  ...stop,
                  poiName: props.departurePlace.poiName,
                  poiStopName: props.departurePlace.poiStopName,
                  address: props.departurePlace.address,
                }
          )
        : booking.stops.map((stop, index) =>
            !index
              ? {
                  ...stop,
                  poiName: props.departurePlace.poiName,
                  poiStopName: props.departurePlace.poiStopName,
                  address: props.departurePlace.address,
                }
              : {
                  ...stop,
                  poiName: props.arrivalPlace.poiName,
                  poiStopName: props.arrivalPlace.poiStopName,
                  address: props.arrivalPlace.address,
                }
          ),
    }));
    opsZoneId.value = bookings.value[0].opsZoneId;
    const opsZoneSearchRequest = buildOpsZoneSearchRequest();
    const opsZoneData = await OpsZoneService.opsZoneSearch(
      opsZoneSearchRequest
    );
    const [opsZone] = opsZoneData.data.opsZones;
    addOpsZoneOptionsToTrip(opsZone);
  } catch (e) {
    await emit('handleServerError', e);
  }
  isBookingInfoLoading.value = false;
}

loadConfirmation();
</script>
<style lang="scss" scoped>
.infoblock {
  margin-top: unset !important;
}
</style>
